import { useEffect, useRef, useState } from 'react';
import {
    ANSWER_OPTIONS,
    QUESTIONNAIRE_ITEMS,
} from '../../lib/plan/questionnaire';
import { AnswerValue, PlanAnswers } from '../../lib/plan/types';
import { cn } from '../../lib/helpers';

interface QuestionnaireStepProps {
    onSubmit: (answers: PlanAnswers) => void;
}

// Krátká prodleva, aby byl vidět zvýrazněný výběr, než se posune na další otázku.
const ADVANCE_DELAY_MS = 320;
const FADE_MS = 160;

export function QuestionnaireStep({ onSubmit }: QuestionnaireStepProps) {
    const [answers, setAnswers] = useState<PlanAnswers>({});
    const [index, setIndex] = useState(0);
    const [entered, setEntered] = useState(true);
    const [locked, setLocked] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
        undefined
    );

    const item = QUESTIONNAIRE_ITEMS[index];
    const total = QUESTIONNAIRE_ITEMS.length;
    const progress = Math.round((index / total) * 100);
    const isLast = index + 1 >= total;
    const selected = answers[item.id];

    useEffect(() => () => clearTimeout(timerRef.current), []);

    function goTo(nextIndex: number) {
        setEntered(false);
        timerRef.current = setTimeout(() => {
            setIndex(nextIndex);
            setEntered(true);
            setLocked(false);
        }, FADE_MS);
    }

    function handleAnswer(value: AnswerValue) {
        if (locked) return;

        const nextAnswers = { ...answers, [item.id]: value };
        setAnswers(nextAnswers);
        setLocked(true);

        timerRef.current = setTimeout(() => {
            if (isLast) {
                onSubmit(nextAnswers);
                return;
            }
            goTo(index + 1);
        }, ADVANCE_DELAY_MS);
    }

    function handleBack() {
        if (index === 0 || locked) return;
        goTo(index - 1);
    }

    return (
        <div className="mx-auto flex w-full max-w-2xl flex-col">
            <div className="mb-8">
                <h1 className="text-5xl">Jak na tom je vaše třída?</h1>
                <p className="mt-3 text-ink-800">
                    Odpovídejte podle posledních dvou týdnů až jednoho měsíce.
                    Nejde o hodnocení — jen o poznání, kde vaší třídě nejvíc
                    pomůžeme.
                </p>
            </div>

            <div className="mb-2 flex items-center justify-between text-sm text-ink-700">
                <span>
                    Otázka {index + 1} z {total}
                </span>
                <span>{progress} %</span>
            </div>
            <div className="mb-8 h-2 rounded-full bg-cloud-200">
                <div
                    className="h-2 rounded-full bg-product-500 transition-all duration-300"
                    style={{ width: `${Math.max(progress, 3)}%` }}
                />
            </div>

            <div
                className={cn(
                    'rounded-2xl bg-white p-6 shadow-sm transition-all duration-200',
                    entered
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-2 opacity-0'
                )}
            >
                <p className="mb-6 min-h-[64px] text-lg font-medium text-ink-900">
                    {item.text}
                </p>
                <div className="flex flex-col gap-2">
                    {ANSWER_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            disabled={locked}
                            onClick={() => handleAnswer(option.value)}
                            className={cn(
                                'rounded-xl border px-4 py-3 text-left text-sm font-medium transition-all duration-150',
                                selected === option.value
                                    ? 'scale-[1.02] border-product-500 bg-product-100 text-product-600'
                                    : 'border-cloud-300 bg-white text-ink-900 hover:bg-cloud-100'
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-4">
                <button
                    type="button"
                    className={cn(
                        'text-sm font-medium text-ink-700 transition-opacity hover:text-ink-900',
                        index === 0 && 'pointer-events-none opacity-0'
                    )}
                    onClick={handleBack}
                >
                    Zpět na předchozí otázku
                </button>
            </div>
        </div>
    );
}
